import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import IstioService, { exposureErrorDetail } from "../../services/istio.service";
import { IPortCapacity, IPortUsage } from "../../types/api/istio.type";

const Stat = ({ label, value, hint }: { label: string; value: string | number; hint?: string }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Tooltip title={hint || ""}>
      <Box>
        <Typography variant="h5">{value}</Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Tooltip>
  </Grid>
);

type Row = {
  port: number;
  status: "free" | "fresh" | "reserved" | "managed" | "unmanaged";
  usage?: IPortUsage;
};

const STATUS_LABEL: Record<Row["status"], string> = {
  fresh: "Free (never used)",
  free: "Free (recycled)",
  reserved: "Reserving…",
  managed: "In use",
  unmanaged: "In use (created by hand)",
};

const STATUS_COLOR: Record<Row["status"], "success" | "default" | "warning" | "primary" | "info"> = {
  fresh: "success",
  free: "info",
  reserved: "warning",
  managed: "primary",
  unmanaged: "default",
};

export const IstioPorts = () => {
  const [capacity, setCapacity] = useState<IPortCapacity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await IstioService.getPortCapacity();
      setCapacity(data);
      setError(null);
    } catch (e) {
      setError(exposureErrorDetail(e).message || "Could not load port capacity");
      setCapacity(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !capacity) {
    return (
      <Box p={4} textAlign="center">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!capacity) return null;

  const usageByPort = new Map(capacity.used.map((u) => [u.port, u]));
  const reserved = new Set(capacity.reserved ?? []);
  const fresh = new Set(capacity.fresh);

  const rows: Row[] = capacity.declared.map((port) => {
    const usage = usageByPort.get(port);
    if (usage) {
      return { port, status: usage.managed ? "managed" : "unmanaged", usage };
    }
    if (reserved.has(port)) return { port, status: "reserved" };
    return { port, status: fresh.has(port) ? "fresh" : "free" };
  });

  const usedPct = capacity.declared_count
    ? (capacity.used_count / capacity.declared_count) * 100
    : 0;

  return (
    <Box p={2}>
      {!capacity.rbac_ok && (
        <Alert severity="error" sx={{ mb: 2 }}>
          External access is unavailable: {capacity.rbac_error}
        </Alert>
      )}

      {capacity.rbac_ok && capacity.free_count === 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          All {capacity.declared_count} ports on <strong>{capacity.gateway}</strong> are in use.
          New databases will be created without external access until the range is extended
          {capacity.remediation ? ` in ${capacity.remediation.repo}/${capacity.remediation.file}` : ""}.
        </Alert>
      )}

      {capacity.rbac_ok && capacity.low_watermark && capacity.free_count > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Only {capacity.free_count} port{capacity.free_count === 1 ? "" : "s"} left on{" "}
          <strong>{capacity.gateway}</strong>. Plan to extend the range before it runs out.
        </Alert>
      )}

      {(capacity.gateway_only.length > 0 || capacity.service_only.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Configuration drift — these ports are declared in only one place and carry no traffic.
          {capacity.gateway_only.length > 0 && ` Gateway only: ${capacity.gateway_only.join(", ")}.`}
          {capacity.service_only.length > 0 && ` Service only: ${capacity.service_only.join(", ")}.`}
        </Alert>
      )}

      {capacity.undeclared_used.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          VirtualServices are bound to undeclared ports {capacity.undeclared_used.join(", ")} — they
          are not reachable.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Stat label="Declared" value={capacity.declared_count} hint="Ports on both the Gateway and its Service" />
          <Stat label="In use" value={capacity.used_count} />
          <Stat label="Free" value={capacity.free_count} hint="Allocatable right now" />
          <Stat
            label="Never used"
            value={capacity.fresh_count}
            hint="Preferred first, so a new tenant does not inherit a previous tenant's host:port"
          />
          <Stat label="Next port" value={capacity.next_port ?? "—"} />
          <Grid item xs={6} sm={4} md={2}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Grid>
          <Grid item xs={12}>
            <LinearProgress
              variant="determinate"
              value={usedPct}
              color={capacity.free_count === 0 ? "error" : capacity.low_watermark ? "warning" : "primary"}
            />
            <Typography variant="caption" color="text.secondary">
              {capacity.gateway}
              {capacity.address ? ` · ${capacity.address}` : ""} · watermark {capacity.watermark}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: 520, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Port</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>VirtualService</TableCell>
              <TableCell>Destination</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.port} hover>
                <TableCell>{row.port}</TableCell>
                <TableCell>
                  <Chip size="small" label={STATUS_LABEL[row.status]} color={STATUS_COLOR[row.status]} />
                </TableCell>
                <TableCell>{row.usage?.env_id ?? "—"}</TableCell>
                <TableCell>
                  {row.usage ? `${row.usage.namespace}/${row.usage.virtual_service}` : "—"}
                </TableCell>
                <TableCell sx={{ fontSize: 12 }}>{row.usage?.destination ?? "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default IstioPorts;
